import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import DateRangePicker from '../components/DateRangePicker';
import Loader from "../components/Loader/Loader";
import apiRequester from '../utils/apiClient';

function CheckMobile() {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [mobileNumber, setMobileNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [templateData, setTemplateData] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [searchText, setSearchText] = useState('');
  const PageNo = [5, 10, 20, 50];

  const formatDate = (date) => {
    if (!date) return "";

    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const handleShow = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams();
      if (fromDate) params.append("FromDate", formatDate(fromDate));
      if (toDate) params.append("ToDate", formatDate(toDate));
      if (mobileNumber) params.append('MobNo', mobileNumber);

      const response = await apiRequester('GET', `Report/GetMobileNoData?${params.toString()}`);

      setTemplateData(
        (Array.isArray(response) ? response : []).map(item => ({
          PROFILEID: item.PROFILEID,
          DEALERCODE: item.DEALERCODE,
          SENDERID: item.SENDERID,
          TEMPLATEID: item.TEMPLATEID,
          MSGID: item.MSGID,
          MOBILE: item.MOBILE,
          SENTDATETIME: item.SENTDATETIME,
          DLVRSTATUS: item.DLVRSTATUS,
          DLVRTIME: item.DLVRTIME,
          Err_Description: item.Err_Description,
          SMSTEXT: item.SMSTEXT
        }))
      );

    } catch (err) {
      setError(
        err?.response?.data?.message ||
        'Failed to fetch data'
      );
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const safePageSize = Number(itemsPerPage) > 0 ? Number(itemsPerPage) : 10;



  const handlePageSizeChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(Math.min(Math.max(page, 1), totalPages));
  };

  const filteredData = templateData.filter((item) => {
    const search = searchText.toLowerCase();

    return (
      item.PROFILEID?.toLowerCase().includes(search) ||
      item.DEALERCODE?.toLowerCase().includes(search) ||
      item.SENDERID?.toLowerCase().includes(search) ||
      item.TEMPLATEID?.toString().toLowerCase().includes(search) ||
      item.MSGID?.toString().toLowerCase().includes(search) ||
      item.MOBILE?.toString().toLowerCase().includes(search) ||
      item.SENTDATETIME?.toLowerCase().includes(search) ||
      item.DLVRSTATUS?.toLowerCase().includes(search) ||
      item.DLVRTIME?.toLowerCase().includes(search) ||
      item.Err_Description?.toLowerCase().includes(search) ||
      item.SMSTEXT?.toLowerCase().includes(search)
    );
  });

  const requestSort = (key) => {
    setCurrentPage(1);
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    const sorted = [...filteredData].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      const aStr = aVal === null || aVal === undefined ? '' : aVal.toString();
      const bStr = bVal === null || bVal === undefined ? '' : bVal.toString();

      const aNum = parseFloat(aStr.replace(/[^0-9.\-]/g, ''));
      const bNum = parseFloat(bStr.replace(/[^0-9.\-]/g, ''));

      let cmp;
      if (!isNaN(aNum) && !isNaN(bNum)) {
        cmp = aNum - bNum;
      } else {
        cmp = aStr.toLowerCase().localeCompare(bStr.toLowerCase());
      }

      return sortConfig.direction === 'ascending' ? cmp : -cmp;
    });
    return sorted;
  }, [filteredData, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / safePageSize));

  const paginatedData = sortedData.slice(
    (currentPage - 1) * safePageSize,
    currentPage * safePageSize
  );


  useEffect(() => {
    setCurrentPage(1);
  }, [templateData, searchText, itemsPerPage]);

  const escapeCSV = (value) => {
    if (value === null || value === undefined) return "";

    const str = value.toString();

    // Escape quotes by doubling them
    const escaped = str.replace(/"/g, '""');

    // Wrap if contains special characters
    if (str.includes(",") || str.includes("\n") || str.includes('"')) {
      return `"${escaped}"`;
    }

    return escaped;
  };

  const downloadCSV = () => {
    const data = filteredData.length ? filteredData : templateData;

    if (!data.length) {
      alert("No data available to download");
      return;
    }

    const headers = [
      "PROFILEID",
      "DEALERCODE",
      "SENDERID",
      "TEMPLATEID",
      "MSGID",
      "MOBILE",
      "SENTDATETIME",
      "DLVRSTATUS",
      "DLVRTIME",
      "Err_Description",
      "SMSTEXT"
    ];

    const rows = data.map(item => [
      escapeCSV(item.PROFILEID),
      escapeCSV(item.DEALERCODE),
      escapeCSV(item.SENDERID),
      escapeCSV(item.TEMPLATEID),
      escapeCSV(item.MSGID),
      escapeCSV(item.MOBILE),
      escapeCSV(item.SENTDATETIME),
      escapeCSV(item.DLVRSTATUS),
      escapeCSV(item.DLVRTIME),
      escapeCSV(item.Err_Description),
      escapeCSV(item.SMSTEXT)
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;"
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "MobileReport.csv");

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // public void ConvertIntoRows(data) {
  //   if(data == null || data.length === 0) {
  //     console.log('No data available');
  //     return;
  //   }
  //   else {
  //   console.log('Data received:', data);
  // }

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <div className="flex min-h-screen">
        <Sidebar />

        <div className="flex-1">
          <Topbar />

          {loading && <Loader />}

          <main className="p-6 md:p-8">
            <div className="rounded-lg border border-slate-200 bg-white/95 px-4 py-4 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.25)] backdrop-blur-sm">
              <div className="grid gap-4 md:grid-cols-[1fr_1fr_auto_auto] md:items-end">
                <label className="block flex flex-col gap-2">
                  <span className="text-xs font-medium text-slate-600">
                    Date Range
                  </span>

                  <DateRangePicker
                    value={{ fromDate, toDate }}
                    onChange={({ fromDate: f, toDate: t }) => {
                      setFromDate(f);
                      setToDate(t);
                    }}
                    placeholder="DD-MM-YYYY ~  DD-MM-YYYY"
                    displayFormat="DD-MM-YYYY"
                    classNames="text-sm z-90 w-full border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                    showFooter={true}
                    configs={{ footer: { cancel: 'Cancel', apply: 'Apply' } }}
                  />
                </label>

                <label className="flex flex-col gap-2">
                  <span className="text-xs font-medium text-slate-600">Mobile Number *</span>
                  <input
                    type="text"
                    value={mobileNumber}
                    onChange={(e) => setMobileNumber(e.target.value)}
                    placeholder="Enter mobile number"
                    className="text-sm w-full border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                  />
                </label>

                <button
                  type="button"
                  onClick={handleShow}
                  disabled={loading}
                  className="rounded bg-slate-700 px-6 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Loading...' : 'Show'}
                </button>
                <button className='rounded bg-slate-700 px-6 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-50' onClick={() => window.location.reload()}>
                  Reset
                </button>
              </div>

              {error && (
                <div className="mt-4 text-sm text-red-600">
                  {error}
                </div>
              )}
            </div>
            {paginatedData.length > 0 && (
              <div className="border border-slate-200 bg-white/95 px-2 sm:px-4 py-4 sm:py-6 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.25)] backdrop-blur-sm rounded-lg">

                <div className="mb-3 flex items-center gap-1 justify-between">
                  <div className="flex items-center gap-2">
                    <select
                      value={itemsPerPage}
                      onChange={handlePageSizeChange}
                      className="w-[80px] border border-slate-200 bg-slate-50 px-3 py-2 text-sm rounded"
                    >
                      {PageNo.map((pageNo) => (
                        <option key={pageNo} value={pageNo}>
                          {pageNo}
                        </option>
                      ))}
                    </select>

                    <span className="text-sm text-slate-600">
                      TOTAL RECORD : {templateData.length}
                    </span>
                  </div>
                  <input
                    type="text"
                    value={searchText}
                    onChange={handleSearchChange}
                    placeholder="Search Here"
                    className="text-sm w-150 border border-slate-200 bg-slate-50 px-3 py-2 rounded focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                  />
                </div>
                <div className="overflow-auto max-h-[300px] mt-3">

                  <table className="w-full overflow-auto min-w-[800px] table-auto border border-slate-200 bg-white text-xs">
                    <thead className="bg-slate-100 sticky top-0 z-10">
                      <tr>
                        <th className="border px-1 py-2">S.No</th>
                        <th onClick={() => requestSort('PROFILEID')} className="border px-1 py-2 cursor-pointer select-none">ProfileID{sortConfig.key === 'PROFILEID' ? (sortConfig.direction === 'ascending' ? ' ↑' : ' ↓') : ''}</th>
                        <th onClick={() => requestSort('DEALERCODE')} className="border px-1 py-2 cursor-pointer select-none">DealerCode{sortConfig.key === 'DEALERCODE' ? (sortConfig.direction === 'ascending' ? ' ↑' : ' ↓') : ''}</th>
                        <th onClick={() => requestSort('SENDERID')} className="border px-1 py-2 cursor-pointer select-none">SenderID{sortConfig.key === 'SENDERID' ? (sortConfig.direction === 'ascending' ? ' ↑' : ' ↓') : ''}</th>
                        <th onClick={() => requestSort('TEMPLATEID')} className="border px-1 py-2 cursor-pointer select-none">TemplateID{sortConfig.key === 'TEMPLATEID' ? (sortConfig.direction === 'ascending' ? ' ↑' : ' ↓') : ''}</th>
                        <th onClick={() => requestSort('MSGID')} className="border px-1 py-2 cursor-pointer select-none">MsgID{sortConfig.key === 'MSGID' ? (sortConfig.direction === 'ascending' ? ' ↑' : ' ↓') : ''}</th>
                        <th onClick={() => requestSort('MOBILE')} className="border px-1 py-2 cursor-pointer select-none">Mobile{sortConfig.key === 'MOBILE' ? (sortConfig.direction === 'ascending' ? ' ↑' : ' ↓') : ''}</th>
                        <th onClick={() => requestSort('SENTDATETIME')} className="border px-1 py-2 cursor-pointer select-none">SentDateTime{sortConfig.key === 'SENTDATETIME' ? (sortConfig.direction === 'ascending' ? ' ↑' : ' ↓') : ''}</th>
                        <th onClick={() => requestSort('DLVRSTATUS')} className="border px-1 py-2 cursor-pointer select-none">Delivery Status{sortConfig.key === 'DLVRSTATUS' ? (sortConfig.direction === 'ascending' ? ' ↑' : ' ↓') : ''}</th>
                        <th onClick={() => requestSort('DLVRTIME')} className="border px-1 py-2 cursor-pointer select-none">Delivery Time{sortConfig.key === 'DLVRTIME' ? (sortConfig.direction === 'ascending' ? ' ↑' : ' ↓') : ''}</th>
                        <th onClick={() => requestSort('Err_Description')} className="border px-1 py-2 cursor-pointer select-none">Error Description{sortConfig.key === 'Err_Description' ? (sortConfig.direction === 'ascending' ? ' ↑' : ' ↓') : ''}</th>
                        <th onClick={() => requestSort('SMSTEXT')} className="border px-1 py-2 cursor-pointer select-none">SMS Text{sortConfig.key === 'SMSTEXT' ? (sortConfig.direction === 'ascending' ? ' ↑' : ' ↓') : ''}</th>
                      </tr>
                    </thead>

                    <tbody key={currentPage}>
                      {paginatedData.map((item, index) => (
                        <tr key={index} className="hover:bg-slate-50">
                          <td className="border px-1 py-1 text-center">
                            {(currentPage - 1) * itemsPerPage + index + 1}
                          </td>

                          <td className="border px-1 py-1">{item.PROFILEID}</td>
                          <td className="border px-1 py-1">{item.DEALERCODE}</td>
                          <td className="border px-1 py-1">{item.SENDERID}</td>
                          <td className="border px-1 py-1">{item.TEMPLATEID}</td>
                          <td className="border px-1 py-1">{item.MSGID}</td>
                          <td className="border px-1 py-1">{item.MOBILE}</td>
                          <td className="border px-1 py-1">{item.SENTDATETIME}</td>
                          <td className="border px-1 py-1">{item.DLVRSTATUS}</td>
                          <td className="border px-1 py-1">{item.DLVRTIME}</td>
                          <td className="border px-1 py-1">{item.Err_Description}</td>
                          <td className="border px-1 py-1">{item.SMSTEXT}</td>
                        </tr>
                      ))}
                    </tbody>

                  </table>
                </div>


                <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
                  {/* Simple Pagination */}
                  <div className="flex items-center gap-3 ">

                    {/* Prev */}
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      ←
                    </button>

                    {/* Current Page */}
                    <span className="px-3 py-1 border rounded bg-cyan-500 text-white">
                      {currentPage}
                    </span>

                    {/* Next */}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      →
                    </button>

                    <span className="text-sm text-slate-600">
                      {currentPage} of {totalPages}
                    </span>

                  </div>
                  <button
                    onClick={downloadCSV}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded"
                  >
                    Download CSV
                  </button>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

export default CheckMobile;
